/**
 * A component that renders the calendar (a date-picker).
 */
import React from "react";
import moment from "moment";
import { connect } from "react-redux";
import styled from "styled-components";

import { getActiveModule, getSameDayTour, getTimesAvailable, getTimezoneName, getTimezoneOffset } from "../../store";

import { deepChange, goToModule, sendCurrentModuleAnswer, getTheme } from "../../actions";

import { Arrow } from "../../lib/icons";

import CalendarDay from "./CalendarDay";
import { MessageTypes } from "../../lib/types";
import { formatDate, urlConfig } from "../../lib/utils";
import { addMessage } from "../messages/actions";
import type { GlobalState } from "../../lib/types";
import { getDisabledDays } from "./selectors";
import { monthNames } from "./constants";

class FamilyAssetsCalendar extends React.Component {
  constructor(props) {
    super(props);
    const { daysCount, beforeToday } = this.props;
    const startDay = urlConfig.calendarTesting ? moment("01-01-2030", "MM-DD-YYYY") : moment();
    let selectedDay = startDay.clone();

    const startTimes = this.getAllTimeSlots();
    if (startTimes.filter((time) => this.isTimeSlotAvailable(time, startDay)).length === 0) {
      selectedDay = selectedDay.add(1, "day");
    }

    this.state = {
      daysCount: daysCount || 4,
      error: false,
      selectedDay,
      startDay,
      selectedTime: null,
      beforeToday: beforeToday !== undefined ? beforeToday : false,
    };
  }

  submitDateTime = (date, time) => {
    const { dispatch, activeModule } = this.props;

    dispatch(sendCurrentModuleAnswer(`${date} at ${time}`));
    dispatch(deepChange({ path: ["userData", "date"], value: date }));
    dispatch(deepChange({ path: ["userData", "tour_time"], value: time }));
    dispatch(
      addMessage({
        type: MessageTypes.USER_MESSAGE,
        text: `${date} at ${time}`,
      })
    );
    dispatch(goToModule(activeModule.action));
  };

  daySelect = (day) => {
    this.setState({
      selectedDay: day,
      selectedTime: null,
    });
  };

  timeSelect = (time) => {
    if (!this.isTimeSlotAvailable(time)) {
      return;
    }
    this.setState({ selectedTime: time.format("h:mm A") });
  };

  prevWeek = () => {
    this.setState((prevState) => ({
      startDay: prevState.startDay.clone().add(-prevState.daysCount, "days"),
      selectedDay: null,
    }));
  };

  nextWeek = () => {
    const { startDay, daysCount } = this.state;
    this.setState({
      startDay: startDay.clone().add(daysCount, "days"),
      selectedDay: null,
    });
  };

  checkUnavailable = (day) => {
    const { beforeToday } = this.state;
    const { disabledDays } = this.props;

    const isPassedDate = !beforeToday && day.isBefore(moment(), "day");
    const isDayDisabled = disabledDays.includes(day.day());

    if (isPassedDate || isDayDisabled) {
      return true;
    }

    return false;
  };

  checkTimeSelected = (time) => {
    const { selectedTime } = this.state;

    if (selectedTime === time.format("h:mm A")) {
      return "selected";
    }
    return "";
  };

  checkSelectedDay = (day) => {
    const { selectedDay } = this.state;
    return Boolean(day.isSame(selectedDay, "day"));
  };

  getMonthAndYear = () => {
    const { selectedDay, startDay } = this.state;
    const date = selectedDay || startDay;
    const monthName = monthNames[date._d.getMonth()]; // eslint-disable-line;
    return `${monthName} ${date._d.getFullYear()}`; // eslint-disable-line;
  };

  showError = () => {
    this.setState({ error: true });
  };

  /**
   * Confirm button click handler.
   */
  confirmClick = () => {
    const { selectedDay, selectedTime } = this.state;

    const disabled = !(selectedDay && selectedTime);

    if (disabled) {
      this.showError();
    } else {
      this.submitDateTime(
        formatDate(selectedDay._d), // eslint-disable-line
        selectedTime
      );
    }
  };

  renderDays(weekdays) {
    const { todayText, unavailableText } = this.props;

    const dayComps = weekdays.map((day) => {
      return (
        <CalendarDay
          key={day._d.toString()}
          day={day}
          todayText={todayText}
          unavailableText={unavailableText}
          onSelected={this.daySelect}
          selected={this.checkSelectedDay(day)}
          unavailable={this.checkUnavailable(day)}
        />
      );
    });

    return <DaysContainer>{dayComps}</DaysContainer>;
  }

  /**
   * Return `true` if provided time is available for booking for the `selectedDay`.
   * @param time - Moment time.
   * @returns {boolean}
   */
  isTimeSlotAvailable = (time, dayToCheck = null, props = {}) => {
    const { selectedDay } = this.state || {};
    const { timesAvailable, timezoneOffset } = this.props || props;

    if (!dayToCheck) {
      dayToCheck = selectedDay;
    }

    if (!dayToCheck) {
      return false;
    }

    // Passing true will keep the same local time, but at the expense of choosing a different point in Universal Time.
    time.utcOffset(timezoneOffset, true);

    // Passing false (the default) will keep the same instant in Universal Time, but the local time will change.
    const now = moment().utcOffset(timezoneOffset);

    const weekDay = dayToCheck && dayToCheck.isoWeekday() - 1; // 0 - Monday.
    const isTodaySelected = dayToCheck && dayToCheck.isSame(now, "day");

    if (dayToCheck && weekDay in timesAvailable) {
      // Whoa! JS supports such syntax!
      let isAvailable = false;
      const todaySlots = timesAvailable[weekDay];
      const hour = time.hour();
      for (let i = 0; i < todaySlots.length; i++) {
        const timeSlot = todaySlots[i];
        const [timeStart, timeEnd] = timeSlot;
        if (timeStart <= hour && hour < timeEnd) {
          // Stupid damn language.
          isAvailable = true;
          break;
        }
      }
      return isAvailable;
    }
    return !isTodaySelected || time.diff(now, "hour") >= 1;
  };

  /**
   * Render time slots.
   */
  renderTimes(times) {
    const timeComps = times.map((time) => {
      let renderedTime = time.format("h:mm A");
      let timeAvailableClass = "";

      if (!this.isTimeSlotAvailable(time)) {
        renderedTime = "-";
        timeAvailableClass = "unavailable";
      }

      return (
        <Time
          key={time._d.toLocaleTimeString()}
          onClick={(e) => this.timeSelect(time)}
          className={`${this.checkTimeSelected(time)} ${timeAvailableClass} time-option`}
        >
          {renderedTime}
        </Time>
      );
    });
    return <TimeContainer className="time">{timeComps}</TimeContainer>;
  }

  /**
   * Get all possible time slots - from 9 a.m. to 4 p.m.
   */
  getAllTimeSlots = () => {
    let timeSlotsStart = moment().hour(9).minute(0).second(0).millisecond(0); // 9 a.m.

    const startTimes = [timeSlotsStart.clone()];
    for (let i = 0; i <= 6; i++) {
      timeSlotsStart = timeSlotsStart.clone().add(1, "hour");
      startTimes.push(timeSlotsStart);
    }
    return startTimes;
  };

  /**
   * Main render method.
   */
  render() {
    const { selectedDay, startDay, daysCount, selectedTime, error } = this.state;

    const { theme, timezoneName } = this.props;

    const weekdays = [];
    for (let index = 0; weekdays.length < daysCount; index += 1) {
      const day = startDay.clone().add(index, "days");
      weekdays.push(day);
    }

    const startTimes = this.getAllTimeSlots();
    const days = this.renderDays(weekdays);
    const times = this.renderTimes(startTimes);
    const disabled = selectedDay && selectedTime ? "" : "disabled";

    let timezoneSuffix = "";
    if (timezoneName) {
      timezoneSuffix = `in ${timezoneName} timezone`;
    }

    return (
      <Container>
        <Slider>
          <Nav>
            <h3>{this.getMonthAndYear()}</h3>
          </Nav>
          <Arrow onClick={this.prevWeek} className="arrow left" fillColor={theme.brandColor} />
          <Arrow onClick={this.nextWeek} className="arrow right" fillColor={theme.brandColor} />

          {days}
          <h4>Select Date and Time {timezoneSuffix}</h4>
          {times}

          {error ? <ErrorMessage>Please select a date and time</ErrorMessage> : null}

          <ConfirmButton className={`${disabled} calendar-confirm`} onClick={this.confirmClick}>
            Next
          </ConfirmButton>
        </Slider>
      </Container>
    );
  }
}

const ErrorMessage = styled.div`
  margin: 3px 0;
  font-size: 13px;
  color: red;
`;

const ConfirmButton = styled.div`
  background: ${(props) => props.theme.brandColor};
  border-radius: 6px;
  width: 100%;
  text-align: center;
  padding: 15px 0;
  margin: 10px 0 0;
  color: #fff;
  cursor: pointer;

  &.disabled {
    background: #eaeff6;
    cursor: not-allowed;
    color: ${(props) => props.theme.brandColor};
  }
`;

const Time = styled.div`
  font-style: normal;
  text-align: center;
  letter-spacing: 0.28px;
  color: #595d61;
  width: 33%;
  margin: 5px 0;
  padding: 5px 0;
  border-radius: 30px;
  font-size: 14px;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  cursor: pointer;
  justify-content: center;
  align-items: center;

  &.selected {
    background: ${(props) => props.theme.brandColor};
    color: #fff;
  }

  &:hover:not(.selected) {
    background-color: rgba(${(props) => props.theme.brandColorRGB}, 0.09);
  }

  &.unavailable {
    cursor: not-allowed;
  }
`;

const DaysContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 28px;
  border-top: 1px solid rgb(34, 59, 117, 0.12);
  border-bottom: 1px solid rgb(34, 59, 117, 0.12);
`;

const TimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  &.time {
    flex-wrap: wrap;
    justify-content: left;
    align-items: left;
  }
`;

const Slider = styled.div`
  mix-blend-mode: normal;

  h4 {
    margin: 5px 0;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 19px;
    text-align: left;
    padding-left: 10px;
    letter-spacing: 0.245px;
    color: #6d7a84;
  }

  .arrow {
    position: absolute;
    top: 70px;
    cursor: pointer;

    border-radius: 50%;
    z-index: 1;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: rgba(${(props) => props.theme.brandColorRGB}, 0.09);
    }
  }

  .arrow.right {
    transform: rotate(180deg);
    right: 9px;
  }

  .arrow.left {
    left: 9px;
  }
`;

const Nav = styled.div`
  position: relative;

  h3 {
    margin: 0;
    padding: 10px 0;
    font-style: normal;
    font-weight: 800;
    font-size: 16px;
    line-height: 22px;
    /* identical to box height */
    text-align: center;
    letter-spacing: 0.28px;
  }
`;

const Container = styled.div`
  display: block;
  position: relative;
  padding: 10px 10px;
  max-width: 100%;
  min-width: 310px;
  margin: 5px 0;
  background: #ffffff;
  box-shadow: 0 2px 9px rgba(217, 217, 217, 0.5);
  border-radius: 11px;
  user-select: none;

  * {
    user-select: none;
  }
`;

function mapStateToProps(state: GlobalState, ownProps) {
  return {
    activeModule: getActiveModule(state),
    theme: getTheme(state),
    timesAvailable: getTimesAvailable(state),
    timezoneName: getTimezoneName(state),
    timezoneOffset: getTimezoneOffset(state),
    sameDayTour: getSameDayTour(state),
    disabledDays: getDisabledDays(state),
  };
}

export default connect(mapStateToProps)(FamilyAssetsCalendar);
